'use client';

import React, { FormEvent, useState } from 'react';

import { Rating, RoundedStar } from '@smastrom/react-rating';

import {
  Text,
  Flex,
  AspectRatio,
  Input,
  Textarea,
  Box,
} from '@chakra-ui/react';
import { Button } from '@chakra-ui/react';
import { Stack } from '@chakra-ui/react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { getTrackingStatus } from '@/_utils/getStatus';

import reviewService from '@/_service/review.service';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { IProps } from '../_types';
import {
  DialogCloseTrigger,
  DialogContent,
  DialogRoot,
} from '@/components/ui/dialog';
import { CField } from '@/_components/CField';
import { toaster } from '@/components/ui/toaster';
import { containsReviewUrl } from '@/_utils/review-text';

function Desktop({ review, productId, orderData, product, refetch }: IProps) {
  const [rate, setRate] = useState(5);
  const [isLoading, setLoading] = useState(false);
  const [isOpen, setOpenModal] = useState(false);
  const [reviewMsg, setReviewMsg] = useState('');
  const [clientMail, setClientMail] = useState('');

  const t = useTranslations();
  const currentLocale = useCustomLocale();
  const ratingLabels = t.raw('rating_labels');

  const onSelectRate = (newValue: any) => {
    setRate(newValue);
    // setOpenModal(true)
  };

  const onClose = () => {
    setOpenModal(false);
  };

  const onSubmitReview = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (containsReviewUrl(reviewMsg)) {
      toaster.warning({
        title: t('review_links_not_allowed'),
      });
      return;
    }

    setLoading(true);

    const data = {
      branch_id: orderData?.steps?.[0]?.branch_id,
      branch_name: orderData?.steps?.[0]?.branch_name,
      client_id: orderData?.client_id,
      client_mail: clientMail,
      client_name: orderData?.client_name,
      courier_id: orderData?.courier_id,
      courier_name: orderData?.courier_id
        ? `${orderData?.courier?.first_name} ${orderData?.courier?.last_name}`
        : '',
      client_phone: orderData?.client_phone_number,
      delivery_time: orderData?.delivery_time,
      lang: currentLocale,
      operator_id: orderData?.shipper_user?.id,
      operator_name: orderData?.shipper_user?.name,
      order_id: orderData?.id,
      order_step_id: orderData?.steps?.[0]?.id,
      product_id: productId,
      order_num: orderData?.external_order_id,
      rating: rate,
      related_subject: 'product',
      review_message: reviewMsg,
      type: rate > 3 ? 'like' : 'dislike',
    };

    reviewService
      .create(data)
      .then(() => {
        setOpenModal(false);
        refetch();
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  };

  if (
    orderData?.status_id &&
    getTrackingStatus(orderData?.status_id) !== 'delivered' &&
    getTrackingStatus(orderData?.status_id) !== 'finished'
  ) {
    return null;
  }

  return (
    <>
      {!Boolean(review?.id) && (
        <Text
          fontSize={14}
          cursor={'pointer'}
          onClick={() => setOpenModal(true)}
          color={'#4E60FF'}
        >
          {t('leave_feedback')}
        </Text>
      )}

      <DialogRoot
        placement={'center'}
        lazyMount
        open={isOpen}
        onOpenChange={() => onClose()}
      >
        <DialogContent rounded={12} p={6}>
          <form onSubmit={(e) => onSubmitReview(e)}>
            <Box color={'text'}>
              <Box mb={4}>
                <Text fontSize={18} textAlign={'center'} fontWeight={700}>
                  {t('leave_feedback')}
                </Text>
              </Box>
              <Box>
                <Flex gap={4} alignItems={'center'}>
                  <Box w={'64px'} h={'64px'} rounded={12} bg={'background'}>
                    {product?.image && (
                      <AspectRatio
                        minW='64px'
                        ratio={1 / 1}
                        overflow='hidden'
                        rounded={12}
                        position='relative'
                      >
                        <Image
                          src={process.env.BASE_URL + product?.image}
                          alt={product?.name}
                          style={{ objectFit: 'cover' }}
                          fill
                        />
                      </AspectRatio>
                    )}
                  </Box>
                  <Text fontSize={16} fontWeight={500}>
                    {product?.name}
                  </Text>
                </Flex>
                <Flex
                  py={4}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Rating
                    readOnly={Boolean(review?.id)}
                    style={{ maxWidth: 120 }}
                    value={!review?.id ? rate : review?.rating}
                    itemStyles={{
                      itemShapes: RoundedStar,
                      activeFillColor: '#FF9910',
                      inactiveFillColor: '#E0E8F1',
                    }}
                    onChange={onSelectRate}
                  />
                  <Text fontSize={12} color={'text.muted'}>
                    {ratingLabels[rate - 1]}
                  </Text>
                </Flex>
                <Stack gap={4}>
                  <CField label={t('email')} bgColor='background'>
                    <Input
                      type='email'
                      value={clientMail}
                      rounded={12}
                      size={'lg'}
                      bgColor='background'
                      pt={6}
                      placeholder=''
                      className='peer'
                      name='email'
                      onChange={(e) => setClientMail(e.target.value)}
                      _focus={{
                        outline: 'var(--primary-default)',
                        borderColor: 'var(--primary-default)',
                        boxShadow: '0px 0px 0px 4px #7E5FA60F',
                      }}
                      _active={{
                        outline: 'var(--primary-default)',
                        borderColor: 'var(--primary-default)',
                        boxShadow: '0px 0px 0px 4px #7E5FA60F',
                      }}
                    />
                  </CField>
                  <CField label={t('your_review')} bgColor='background'>
                    <Textarea
                      value={reviewMsg}
                      rounded={12}
                      size={'lg'}
                      bgColor='background'
                      pt={6}
                      placeholder=''
                      className='peer'
                      name='review'
                      onChange={(e) => setReviewMsg(e.target.value)}
                      _focus={{
                        outline: 'var(--primary-default)',
                        borderColor: 'var(--primary-default)',
                        boxShadow: '0px 0px 0px 4px #7E5FA60F',
                      }}
                      _active={{
                        outline: 'var(--primary-default)',
                        borderColor: 'var(--primary-default)',
                        boxShadow: '0px 0px 0px 4px #7E5FA60F',
                      }}
                    />
                  </CField>
                </Stack>
              </Box>
              <Flex mt={6} justifyContent='center'>
                <Button
                  type='submit'
                  rounded={12}
                  size={'xl'}
                  w={'100%'}
                  h={'56px'}
                  bg='primary'
                  color='primary.text'
                  _hover={{
                    bg: 'primary.hover',
                  }}
                  loading={isLoading}
                  disabled={!Boolean(rate)}
                >
                  {t('send')}
                </Button>
              </Flex>
            </Box>
          </form>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  );
}

export default Desktop;
